import { useState } from 'react'
import { useParams } from 'react-router-dom'
import classNames from 'classnames'
import { AlertTriangle, Ban, XCircle } from 'lucide-react'
import Button from '@/components/button'
import ConfirmDialog from '@/components/confirm-dialog'
import DataTable from '@/components/data-table'
import type { Column } from '@/components/data-table'
import DefaultModal from '@/components/default-modal'
import DefinitionList from '@/components/definition-list'
import type { DefinitionItem } from '@/components/definition-list'
import EmptyState from '@/components/empty-state'
import FormField from '@/components/form-field'
import { Input } from '@/components/input'
import PageHeader from '@/components/page-header'
import Skeleton from '@/components/skeleton'
import StatusBadge from '@/components/status-badge'
import Tabs from '@/components/tabs'
import { useCancelarGuiaManualMutation, useCancelarGuiaMutation } from '@/http/mutations/guias'
import {
  useGuiaQuery,
  useHistoricoGuiaQuery,
  usePayloadHistoricoGuiaQuery,
} from '@/http/queries/guias'
import {
  resolveSituacaoGuia,
  resolveSituacaoProcedimento,
  resolveSucessoEvento,
  resolveTipoGuia,
} from '@/types/domain'
import type { HistoricoGuiaEvento } from '@/types/guia'
import { toErrorMessage } from '@/services/errors'
import { useAuth } from '@/utility/context/auth-context'
import { ACAO } from '@/utility/permissions'
import { sanitizeEventoHtml } from '@/utility/sanitize'
import { formatDateDefault, formatDateTimeDefault, orDash } from '@/utility/Utils'
import styles from './style.module.css'

type CancelamentoTipo = 'automatico' | 'manual'

const MOTIVO_MIN = 5
const MOTIVO_MAX = 255

const AutorizacoesDetail = () => {
  const { guiaId } = useParams<{ guiaId: string }>()
  const { can } = useAuth()
  const { data: guia, isPending } = useGuiaQuery(guiaId)

  const [abaAtiva, setAbaAtiva] = useState('dados')
  const [historicoPage, setHistoricoPage] = useState(1)
  const [historicoPageSize, setHistoricoPageSize] = useState(20)
  const [eventoPayloadId, setEventoPayloadId] = useState<string | null>(null)
  const [eventoExpandidoId, setEventoExpandidoId] = useState<string | null>(null)

  const {
    data: historico,
    error: historicoError,
    isFetching: historicoIsFetching,
    refetch: refetchHistorico,
  } = useHistoricoGuiaQuery(guiaId, historicoPage, historicoPageSize, abaAtiva === 'historico')

  const {
    data: payloadTecnico,
    error: payloadError,
    isPending: payloadIsPending,
  } = usePayloadHistoricoGuiaQuery(guiaId, eventoPayloadId ?? undefined)

  const [cancelamento, setCancelamento] = useState<CancelamentoTipo | null>(null)
  const [motivo, setMotivo] = useState('')
  const [motivoErro, setMotivoErro] = useState<string | null>(null)

  const fecharCancelamento = () => {
    setCancelamento(null)
    setMotivo('')
    setMotivoErro(null)
  }

  const ehCancelamentoDireto = guia?.cancelamentoImediato ?? false
  const mensagemSucessoAutomatico = ehCancelamentoDireto
    ? 'Guia cancelada com sucesso!'
    : 'Pedido de cancelamento enviado! A guia ficará em Aguardando Cancelamento até a operadora confirmar.'

  const cancelarAutomatico = useCancelarGuiaMutation(mensagemSucessoAutomatico, fecharCancelamento)
  const cancelarManual = useCancelarGuiaManualMutation(fecharCancelamento)

  const validarMotivo = (): boolean => {
    const valor = motivo.trim()
    if (valor.length < MOTIVO_MIN || valor.length > MOTIVO_MAX) {
      setMotivoErro(`O motivo precisa ter pelo menos ${MOTIVO_MIN} caracteres.`)
      return false
    }
    setMotivoErro(null)
    return true
  }

  if (isPending) {
    return (
      <>
        <PageHeader title="Autorização" backTo="/autorizacoes" />
        <Skeleton variant="text" count={6} />
      </>
    )
  }

  if (!guia) {
    return (
      <>
        <PageHeader title="Autorização" backTo="/autorizacoes" />
        <EmptyState
          title="Autorização não encontrada"
          description="Ela pode ter sido removida ou o identificador está incorreto."
        />
      </>
    )
  }

  const situacao = resolveSituacaoGuia(guia.situacao, guia.nomeSituacao)

  const identificacao: DefinitionItem[] = [
    { label: 'Beneficiário', value: orDash(guia.nomeBeneficiario) },
    { label: 'Carteira', value: orDash(guia.numeroCarteira) },
    { label: 'Plano', value: orDash(guia.planoBeneficiario) },
    { label: 'Operadora', value: orDash(guia.nomeOperadora) },
    { label: 'Credencial', value: orDash(guia.nomeCredencial) },
    { label: 'Tipo de guia', value: resolveTipoGuia(guia.tipoGuia, guia.nomeTipoGuia) },
    { label: 'Guia na operadora', value: orDash(guia.guiaNaOperadora) },
    { label: 'Guia prestador', value: orDash(guia.guiaPrestador) },
    { label: 'Senha', value: orDash(guia.senhaNaOperadora) },
    { label: 'Protocolo', value: orDash(guia.protocolo) },
    { label: 'Emissão', value: formatDateDefault(guia.dataEmissao) },
    { label: 'Autorização', value: formatDateTimeDefault(guia.dataAutorizacao) },
    { label: 'Validade da senha', value: formatDateDefault(guia.validadeSenha) },
    { label: 'Cancelamento', value: formatDateTimeDefault(guia.dataCancelamento) },
  ]

  const diagnostico: DefinitionItem[] = [
    { label: 'Motivo da negativa', value: orDash(guia.motivoNegativa) },
    { label: 'Motivo do cancelamento', value: orDash(guia.motivoCancelamento) },
    { label: 'Mensagens de falha', value: orDash(guia.mensagensFalha) },
    { label: 'Token de autorização', value: orDash(guia.tokenAutorizacao) },
    { label: 'Início da execução', value: formatDateTimeDefault(guia.inicioExecucao) },
    { label: 'Fim da execução', value: formatDateTimeDefault(guia.fimExecucao) },
    { label: 'Tempo de execução', value: orDash(guia.diferencaTempoExecucao) },
    { label: 'Observações', value: orDash(guia.observacoes) },
  ]

  const solicitante: DefinitionItem[] = [
    { label: 'Nome', value: orDash(guia.nomeSolicitante ?? guia.nomeSolicitanteFallBack) },
    {
      label: 'Conselho',
      value: orDash(guia.siglaConselhoSolicitante ?? guia.conselhoSolicitanteFallback),
    },
    {
      label: 'Número do conselho',
      value: orDash(guia.numeroConselhoSolicitante ?? guia.numeroConselhoSolicitanteFallBack),
    },
    { label: 'UF', value: orDash(guia.ufSolicitante ?? guia.ufSolicitanteFallBack) },
    { label: 'Executante', value: orDash(guia.nomeProfissionalExecutante) },
  ]

  const historicoColumns: Column<HistoricoGuiaEvento>[] = [
    {
      key: 'data',
      header: 'Data',
      render: (evento) => formatDateTimeDefault(evento.data),
    },
    { key: 'origem', header: 'Origem', render: (evento) => orDash(evento.origem) },
    { key: 'tipo', header: 'Tipo', render: (evento) => orDash(evento.tipo) },
    {
      key: 'evento',
      header: 'Evento',
      render: (evento) => {
        const expandido = eventoExpandidoId === evento.id
        const eventoLongo =
          (evento.evento?.length ?? 0) > 160 || (evento.eventoHtml?.length ?? 0) > 160

        return (
          <div className={styles.eventoCell}>
            {evento.eventoHtml ? (
              <span
                className={classNames(styles.eventoContent, {
                  [styles.eventoClamped]: !expandido,
                })}
                dangerouslySetInnerHTML={{ __html: sanitizeEventoHtml(evento.eventoHtml) }}
              />
            ) : (
              <span
                className={classNames(styles.eventoContent, {
                  [styles.eventoClamped]: !expandido,
                })}
              >
                {orDash(evento.evento)}
              </span>
            )}

            {eventoLongo ? (
              <Button
                variant="ghost"
                size="sm"
                className={styles.eventoToggle}
                onClick={() => setEventoExpandidoId(expandido ? null : evento.id)}
              >
                {expandido ? 'Ver menos' : 'Ver mais'}
              </Button>
            ) : null}
          </div>
        )
      },
    },
    { key: 'autor', header: 'Autor', render: (evento) => orDash(evento.autor) },
    {
      key: 'sucesso',
      header: 'Resultado',
      render: (evento) => {
        const meta = resolveSucessoEvento(evento)
        if (!meta) return orDash(null)
        return <StatusBadge label={meta.label} tone={meta.tone} />
      },
    },
    {
      key: 'payload',
      header: '',
      align: 'right',
      width: '140px',
      render: (evento) =>
        evento.possuiPayloadTecnico ? (
          <Button variant="ghost" size="sm" onClick={() => setEventoPayloadId(evento.id)}>
            Ver payload
          </Button>
        ) : null,
    },
  ]

  const abas = [
    {
      id: 'dados',
      label: 'Dados da guia',
      content: (
        <div className={styles.sections}>
          <DefinitionList title="Identificação" items={identificacao} />
          <DefinitionList title="Solicitante e executante" items={solicitante} />
          <DefinitionList title="Diagnóstico da operação" items={diagnostico} />
        </div>
      ),
    },
    {
      id: 'procedimentos',
      label: `Procedimentos (${guia.procedimentos.length})`,
      content: guia.procedimentos.length ? (
        <div className={styles.tableCard}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Código</th>
                <th>Procedimento</th>
                <th>Qtd.</th>
                <th>Autorizada</th>
                <th>Executada</th>
                <th>Situação</th>
                <th>Mensagem</th>
              </tr>
            </thead>
            <tbody>
              {guia.procedimentos.map((procedimento, index) => {
                const meta = resolveSituacaoProcedimento(
                  procedimento.situacao,
                  procedimento.nomeSituacao,
                )

                return (
                  <tr key={procedimento.id ?? `${procedimento.codigoProcedimento}-${index}`}>
                    <td>{orDash(procedimento.codigoProcedimento)}</td>
                    <td>{orDash(procedimento.nomeProcedimento)}</td>
                    <td>{procedimento.quantidade}</td>
                    <td>{procedimento.quantidadeAutorizada}</td>
                    <td>{procedimento.quantidadeExecutada}</td>
                    <td>
                      <StatusBadge label={meta.label} tone={meta.tone} />
                    </td>
                    <td>{orDash(procedimento.mensagem ?? procedimento.codigoGlosa)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState title="Esta autorização não tem procedimentos registrados" />
      ),
    },
    {
      id: 'historico',
      label: 'Histórico e logs',
      content: (
        <DataTable
          columns={historicoColumns}
          page={historico}
          getRowKey={(evento) => evento.id}
          isLoading={historicoIsFetching}
          error={historicoError}
          onRetry={() => void refetchHistorico()}
          onPageChange={setHistoricoPage}
          pageSize={historicoPageSize}
          onPageSizeChange={(novoTamanho) => {
            setHistoricoPageSize(novoTamanho)
            setHistoricoPage(1)
          }}
          emptyTitle="Nenhum evento encontrado para esta guia"
          emptyDescription="Ainda não há histórico, timeline ou trace do crawler registrado."
        />
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title={orDash(guia.nomeBeneficiario)}
        subtitle={
          <span className={styles.subtitle}>
            <StatusBadge label={situacao.label} tone={situacao.tone} />
            <span>Guia {orDash(guia.guiaNaOperadora ?? guia.numeroSolicitacao)}</span>
          </span>
        }
        backTo="/autorizacoes"
        actions={
          <>
            {guia.podeCancelarAutomaticamente && can(ACAO.CancelarAutorizacao) ? (
              <Button
                variant="outline"
                iconLeft={<Ban size={16} />}
                onClick={() => setCancelamento('automatico')}
              >
                Cancelar autorização
              </Button>
            ) : null}

            {guia.podeCancelarManualmente && can(ACAO.CancelarAutorizacaoManual) ? (
              <Button
                variant="danger"
                iconLeft={<XCircle size={16} />}
                onClick={() => setCancelamento('manual')}
              >
                Cancelar manualmente
              </Button>
            ) : null}
          </>
        }
      />

      <Tabs items={abas} activeId={abaAtiva} onChange={setAbaAtiva} />

      <DefaultModal
        isOpen={eventoPayloadId !== null}
        toggle={() => setEventoPayloadId(null)}
        title="Payload técnico"
        size="lg"
      >
        {payloadIsPending ? <Skeleton variant="text" count={8} /> : null}

        {!payloadIsPending && payloadError ? (
          <EmptyState
            icon={<AlertTriangle size={28} />}
            title="Não foi possível carregar o payload técnico"
            description={toErrorMessage(payloadError)}
          />
        ) : null}

        {!payloadIsPending && !payloadError ? (
          <pre className={styles.payload}>{payloadTecnico}</pre>
        ) : null}
      </DefaultModal>

      <ConfirmDialog
        isOpen={cancelamento === 'automatico'}
        toggle={fecharCancelamento}
        title="Cancelar autorização"
        message={
          ehCancelamentoDireto
            ? 'Esta guia nunca chegou a ser processada pela operadora. O cancelamento é feito direto no IntegraDoor, é imediato e não pode ser desfeito.'
            : 'O pedido de cancelamento será enviado para processamento. A guia ficará em Aguardando Cancelamento até a operadora confirmar, o que pode levar algum tempo.'
        }
        confirmLabel="Confirmar cancelamento"
        isDestructive
        isLoading={cancelarAutomatico.isPending}
        onConfirm={() => {
          if (!validarMotivo()) return
          cancelarAutomatico.mutate({ guiaId: guia.id as string, motivoCancelamento: motivo.trim() })
        }}
      >
        <FormField
          label="Motivo do cancelamento"
          htmlFor="motivoAutomatico"
          required
          error={motivoErro ?? undefined}
          className={styles.campoMotivo}
        >
          <Input
            id="motivoAutomatico"
            placeholder={`Preencha o motivo (${MOTIVO_MIN} a ${MOTIVO_MAX} caracteres)`}
            value={motivo}
            maxLength={MOTIVO_MAX}
            onChange={(event) => setMotivo(event.target.value)}
          />
        </FormField>
      </ConfirmDialog>

      <ConfirmDialog
        isOpen={cancelamento === 'manual'}
        toggle={fecharCancelamento}
        title="Cancelar manualmente"
        message="Este cancelamento vale apenas no IntegraDoor e não é enviado à operadora. Use somente quando a guia nunca foi gerada no portal, ou quando ela está presa em Aguardando Cancelamento porque o worker não conseguiu confirmar o cancelamento lá. Antes de gravar, o sistema consulta o portal da operadora — é uma chamada externa e pode demorar."
        confirmLabel="Cancelar no IntegraDoor"
        isDestructive
        isLoading={cancelarManual.isPending}
        onConfirm={() => {
          if (!validarMotivo()) return
          cancelarManual.mutate({ guiaId: guia.id as string, motivoCancelamento: motivo.trim() })
        }}
      >
        <FormField
          label="Motivo do cancelamento"
          htmlFor="motivoManual"
          required
          error={motivoErro ?? undefined}
          className={styles.campoMotivo}
        >
          <Input
            id="motivoManual"
            placeholder={`Preencha o motivo (${MOTIVO_MIN} a ${MOTIVO_MAX} caracteres)`}
            value={motivo}
            maxLength={MOTIVO_MAX}
            onChange={(event) => setMotivo(event.target.value)}
          />
        </FormField>

        {cancelarManual.isPending ? (
          <p className={styles.consultaPortal}>
            Consultando o portal da operadora, aguarde...
          </p>
        ) : null}
      </ConfirmDialog>
    </>
  )
}

export default AutorizacoesDetail
